import { Link, useLocation, useSearchParams } from "react-router-dom"
import type { ReactNode } from "react"
import {
  Activity,
  BookOpen,
  Bot,
  BrushCleaning,
  CircleCheckBig,
  ListChecks,
  ShieldQuestion,
  SquareTerminal,
  Building2,
  Feather,
  ChartColumn,
  Gauge,
  Bug,
  Ban,
  Clock,
  FlaskConical,
  FolderKanban,
  ShieldBan,
  Layers,
  MailWarning,
  GraduationCap,
  HeartPulse,
  History,
  Inbox,
  KeyRound,
  LockKeyhole,
  LayoutDashboard,
  Package,
  MailCheck,
  MessageSquareText,
  Radar,
  MapPinned,
  Receipt,
  ReceiptText,
  Send,
  Settings,
  Shield,
  TowerControl,
  UserCog,
  UsersRound,
  Wallet,
  Webhook,
} from "lucide-react"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { SidebarNavBadge } from "@/components/sidebar-nav-badge"
import { useOutreachStats } from "@/hooks/use-outreach"
import { agencyActiveItem, agencyNav, type WorkspaceAccess } from "@/lib/workspaces"
import { setupFirst } from "@/lib/cody-agents"
import { useAgentsSetup, useCodyWaitingCount } from "@/hooks/use-cody-agents"
import { CollapsibleSidebarGroup } from "@/components/collapsible-sidebar-group"
import { EventsSidebar } from "@/components/events/events-sidebar"
import { TasksSidebar } from "@/components/tasks/tasks-sidebar"
import { useProducts } from "@/lib/api/products"
import { useSupportCounts } from "@/lib/api/support"

/**
 * Sidebar menus for the workspaces other than SEO, whose menu stays in
 * App.tsx. Most are a short flat list, like Resend's; Admin holds every
 * admin page, so it is grouped like SEO (config in lib/workspaces.ts).
 */

function useIsActive() {
  const location = useLocation()
  return (path: string) => location.pathname === path || location.pathname.startsWith(path + "/")
}

function Item({ to, label, icon, active, badge, ownerOnly }: { to: string; label: string; icon: ReactNode; active: boolean; badge?: ReactNode; ownerOnly?: boolean }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        tooltip={ownerOnly ? `${label} (owner only)` : label}
        data-owner-only={ownerOnly || undefined}
        // Owner-only pages: a hairline on the left and a faint tint, so the owner
        // can tell at a glance what no admin sees.
        className={ownerOnly ? "relative bg-foreground/[0.025] before:absolute before:inset-y-1.5 before:left-0 before:w-px before:rounded-full before:bg-foreground/30" : undefined}
      >
        <Link to={to} title={ownerOnly ? "Only the owner can see this page" : undefined}>
          {icon}
          <span>{label}</span>
          {badge}
          {ownerOnly && <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70 group-data-[collapsible=icon]:hidden">Owner</span>}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function Menu({ children }: { children: ReactNode }) {
  return (
    <SidebarGroup className="py-1">
      <SidebarGroupContent>
        <SidebarMenu>{children}</SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

/** The Products workspace (owners only): the overview, then one item per product. */
export function ProductsNav() {
  const { pathname } = useLocation()
  const { data } = useProducts(30)
  return (
    <Menu>
      <Item to="/products" label="Overview" icon={<LayoutDashboard />} active={pathname === "/products"} />
      {(data ?? []).map(({ product }) => (
        <Item key={product.slug} to={`/products/${product.slug}`} label={product.name} icon={<Package />} active={pathname === `/products/${product.slug}`} />
      ))}
    </Menu>
  )
}

/**
 * The Support workspace (FOR-350, owners only). The folders are the inbox's
 * ?folder=, and a thread (/support/c/:id) keeps the folder it was opened from.
 */
export function SupportNav() {
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const { data: counts } = useSupportCounts()
  const inInbox = pathname === "/support" || pathname.startsWith("/support/c/")
  const folder = params.get("folder") ?? "inbox"
  const at = (f: string) => inInbox && folder === f
  return (
    <Menu>
      <Item to="/support" label="Inbox" icon={<Inbox />} active={at("inbox")} badge={<SidebarNavBadge count={counts?.unread ?? 0} />} />
      <Item to="/support?folder=waiting" label="Waiting on us" icon={<Clock />} active={at("waiting")} badge={<SidebarNavBadge count={counts?.waiting_on_team ?? 0} />} />
      <Item to="/support?folder=all" label="All" icon={<Layers />} active={at("all") || at("closed")} />
      <Item to="/support?folder=spam" label="Spam" icon={<Ban />} active={at("spam")} />
      <Item to="/support?folder=test" label="Test" icon={<FlaskConical />} active={at("test")} />
      <Item to="/support/projects" label="Projects" icon={<FolderKanban />} active={pathname.startsWith("/support/projects")} />
      <Item to="/support/blocks" label="Blocks" icon={<ShieldBan />} active={pathname.startsWith("/support/blocks")} />
    </Menu>
  )
}

export function TasksNav({ hasLicenses, inProgressProjectCount }: { hasLicenses: boolean; inProgressProjectCount: number }) {
  return <TasksSidebar hasLicenses={hasLicenses} inProgressProjectCount={inProgressProjectCount} />
}

// The admin tab strip had no icons; these were chosen for the sidebar. The
// four items that were already in the Admin sidebar keep theirs.
const AGENCY_ICONS: Record<string, ReactNode> = {
  hq: <TowerControl />,
  overview: <Shield data-anim="pulse" />,
  health: <HeartPulse data-anim="pulse" />,
  usage: <ChartColumn />,
  clients: <Building2 />,
  licenses: <KeyRound />,
  payroll: <Receipt />,
  "client-pulse": <MailCheck />,
  "client-dashboards": <LayoutDashboard />,
  "my-invoices": <ReceiptText />,
  team: <UsersRound />,
  compensation: <Wallet />,
  users: <UserCog />,
  prompts: <MessageSquareText data-anim="lines-chat" />,
  "ai-visibility": <Radar />,
  "local-rank": <MapPinned />,
  "api-activity": <Activity />,
  webhooks: <Webhook />,
  training: <GraduationCap />,
  uptime: <Gauge />,
  "error-tracking": <Bug />,
  "secret-access": <LockKeyhole />,
  "site-email": <MailWarning />,
  docs: <BookOpen data-anim="book" />,
  settings: <Settings />,
}

export function AgencyNav({ access }: { access: WorkspaceAccess }) {
  const { pathname } = useLocation()
  const groups = agencyNav(access)
  const active = agencyActiveItem(pathname, groups)
  return (
    <>
      {groups.map((group) => (
        <CollapsibleSidebarGroup key={group.id} title={group.title}>
          {group.items.map((item) => (
            <Item
              key={item.id}
              to={item.to}
              label={item.label}
              icon={AGENCY_ICONS[item.id]}
              active={active === item.id}
              ownerOnly={item.ownerOnly}
            />
          ))}
        </CollapsibleSidebarGroup>
      ))}
    </>
  )
}

export function StudioNav() {
  const isActive = useIsActive()
  return (
    <Menu>
      <Item to="/studio" label="Creator Studio" icon={<Feather data-anim="quill" />} active={isActive("/studio")} />
    </Menu>
  )
}

export function OutreachNav() {
  const isActive = useIsActive()
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const inbox = pathname === "/outreach" && params.get("tab") === "inbox"
  const unread = useOutreachStats({}).data?.tabs.inbox ?? 0
  return (
    <Menu>
      <Item to="/outreach" label="Queue" icon={<Send data-anim="launch" />} active={pathname === "/outreach" && !inbox} />
      <Item to="/outreach?tab=inbox" label="Inbox" icon={<Inbox />} active={inbox} badge={<SidebarNavBadge count={unread} />} />
      <Item to="/outreach/runs" label="Runs" icon={<History />} active={isActive("/outreach/runs")} />
      <Item to="/outreach/settings" label="Settings" icon={<Settings />} active={isActive("/outreach/settings")} />
    </Menu>
  )
}

/**
 * The Agents workspace (FOR-326, owners only). Setup leads the menu until
 * every required step is done and Cody has been started once; after that it
 * sits at the end.
 */
export function AgentsNav() {
  const { pathname } = useLocation()
  const at = (path: string) => pathname === path || pathname.startsWith(path + "/")
  const waiting = useCodyWaitingCount()
  const { data: setup } = useAgentsSetup(true, 60_000)
  const first = setup ? setupFirst(setup.blocking, setup.settings.started_at as string | null | undefined) : false
  const setupItem = <Item to="/agents/setup" label="Setup" icon={<ListChecks />} active={at("/agents/setup")} badge={first && setup ? <SidebarNavBadge count={setup.blocking.length} /> : undefined} />
  return (
    <>
      <Menu>
        {first && setupItem}
        <Item to="/agents" label="Agents" icon={<Bot data-anim="bot" />} active={pathname === "/agents"} />
        <Item to="/agents/sessions" label="Live sessions" icon={<SquareTerminal />} active={at("/agents/sessions")} />
        <Item to="/agents/runs" label="Runs" icon={<History />} active={at("/agents/runs")} />
        <Item to="/agents/review" label="Waiting" icon={<ShieldQuestion />} active={at("/agents/review")} badge={<SidebarNavBadge count={waiting} />} />
        <Item to="/agents/sweeper" label="Sweeper" icon={<BrushCleaning />} active={at("/agents/sweeper")} />
        <Item to="/agents/shipped" label="Shipped" icon={<CircleCheckBig />} active={at("/agents/shipped")} />
        <Item to="/agents/settings" label="Settings" icon={<Settings />} active={at("/agents/settings")} />
        {!first && setupItem}
      </Menu>
      <CollapsibleSidebarGroup title="Other">
        <Item to="/agents/desk" label="Desk agents" icon={<UsersRound />} active={at("/agents/desk")} />
      </CollapsibleSidebarGroup>
    </>
  )
}

export function EventsNav() {
  return <EventsSidebar />
}
